import type { Case } from '@/content/schema';
import type { Ctx } from '@/core/context';
import { nextRandom, shuffle } from '@/core/rng';
import { fmtClock } from '@/core/time';
import { settleSuspect } from './production';
import { gainXp } from './progress';
import { maybeBeforeInspect } from './events';
import { grantItem } from './items';
import { nextMissionText, currentMission } from './missions';
import { bump } from './achievements';
import { tr } from '@/i18n';

const STARS = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);
export const caseBadge = (k: Case) => ({ text: tr("{0}｜{1}", k.id, STARS(k.difficulty)), color: '#f2b134' });

/** 依角色挑今天的案件：優先沒破過的，再補已破過的（可重玩拿好結局） */
export function planCases(ctx: Ctx): void {
  const { c, day, profile } = ctx;
  const n = c.game.cases.perDay;
  const tags = c.roleById.get(day.roleId)?.caseTags ?? [];
  const mine = c.cases.filter(k => k.roles.some(t => tags.includes(t)));
  const fresh = shuffle(mine.filter(k => !profile.cleared[k.id]), day);
  const again = shuffle(mine.filter(k => profile.cleared[k.id]?.grade === 'bad'), day);
  const rest = shuffle(mine.filter(k => profile.cleared[k.id]?.grade === 'good'), day);
  let pick = [...fresh, ...again, ...rest].slice(0, n);
  if (pick.length < n) pick = [...pick, ...shuffle(c.cases.filter(k => !pick.includes(k)), day)].slice(0, n);
  // 依難度由易到難
  pick.sort((a, b) => a.difficulty - b.difficulty);
  day.caseQueue = pick.map(k => k.id);
  day.activeCase = 0;
  day.cases = Object.fromEntries(pick.map((k, i) => [k.id, { stage: i === 0 ? 'talk' : 'pending', round: 0, best: 0, picks: [], stopped: false, introSeen: false }]));
}

export const activeCase = (ctx: Ctx): Case | null => {
  const id = ctx.day.caseQueue[ctx.day.activeCase];
  return id ? ctx.c.caseById.get(id) ?? null : null;
};

/** 目標要在生產區嗎？（要換裝、氣浴、洗手） */
function targetInProduction(ctx: Ctx, floor: string, target: string): boolean {
  const f = ctx.c.floorById.get(floor as never); if (!f) return false;
  let pos: [number, number] | null = null;
  if (target.startsWith('npc:')) { const n = ctx.c.npcById.get(target.slice(4)); pos = n?.floor === floor && n.pos ? n.pos : null; }
  else { const e = f.equipment.find(q => q.id === target); pos = e ? e.pos : null; }
  if (!pos) return false;
  const z = [...f.zones].reverse().find(q => pos![0] >= q.rect[0] && pos![0] <= q.rect[0] + q.rect[2] && pos![1] >= q.rect[1] && pos![1] <= q.rect[1] + q.rect[3]);
  return !!z && z.level >= 0 && !(z.cold && f.doors.some(d => (d.to === z.id || d.from === z.id) && !d.gate && f.zones.find(q => q.id === (d.to === z.id ? d.from : d.to))!.level < 0));
}

export function objective(ctx: Ctx): string {
  const d = ctx.day;
  if (!d.clockedIn) return tr("⏰ 到 1F 大門旁的打卡鐘打卡上班");
  const k = activeCase(ctx);
  let text = '', floor = '', target = '';
  if (k) {
    const pr = d.cases[k.id];
    const npc = ctx.c.npcById.get(k.npc)!;
    const fl = ctx.c.floorById.get(k.floor)!.name;
    floor = k.floor; target = pr.stage === 'inspect' ? k.equipment : `npc:${k.npc}`;
    if (pr.stage === 'talk') text = tr("{0}：找{1}（{2}）了解狀況", fl, npc.shortName, npc.title);
    else if (pr.stage === 'inspect') text = tr("{0}：到「{1}」確認", fl, shortName(k.targetName));
    else if (pr.stage === 'report') text = tr("{0}：回報{1}", fl, npc.shortName);
    if (pr.stage === 'talk' || pr.stage === 'report') { const a = ctx.c.npcById.get(k.npc); if (!a?.pos) target = k.equipment; }
  } else {
    const cur = currentMission(ctx);
    if (cur) { text = nextMissionText(ctx)!; floor = cur.st.floor; target = cur.st.target; }
    else return d.clock >= d.shiftEnd ? tr("到 1F 廠區外的打卡鐘打卡下班") : tr("今天的工作都完成了。到處逛逛、找幹部聊聊，時間到去 1F 打卡下班");
  }
  if (floor && targetInProduction(ctx, floor, target)) {
    if (!d.dressed) return tr("🥼 先到更衣室換工作服 → 氣浴室 → 洗手。然後：{0}", text);
    const z = ctx.world.rt().zoneAt(...ctx.world.playerPos());
    if (!d.washed && z && z.level === 0 && !z.cold) return tr("🧼 先洗手（洗手 → 酒精 → 烘手機），再推門進去。然後：{0}", text);
  }
  return text;
}
export const shortName = (s: string) => s.split(/[＋、與，+,&]| and /)[0].trim();

function npcCard(ctx: Ctx, k: Case, text: string, choices: Parameters<Ctx['ui']['card']>[0]['choices'], extra: Partial<Parameters<Ctx['ui']['card']>[0]> = {}) {
  const npc = ctx.c.npcById.get(k.npc)!;
  ctx.ui.card({ name: npc.name, role: npc.title, color: npc.color, npc: npc.id, badge: caseBadge(k), text, choices, ...extra });
}

export function talkToCaseNpc(ctx: Ctx, k: Case): void {
  const pr = ctx.day.cases[k.id];
  const npc = ctx.c.npcById.get(k.npc)!;
  if (pr.stage === 'pending') { npcCard(ctx, k, `${npc.catchphrase}`, [{ text: tr("好"), fn: () => ctx.ui.close() }]); return; }
  if (pr.stage === 'done') { npcCard(ctx, k, pr.grade === 'good' ? k.endings.good.line : k.endings.bad.line, [{ text: tr("好"), fn: () => ctx.ui.close() }], { expression: pr.grade === 'good' ? 'happy' : 'worried' }); return; }
  if (pr.stage === 'inspect') { npcCard(ctx, k, tr("先去看「{0}」。我在這邊等你。", k.targetName), [{ text: tr("好，我去"), fn: () => ctx.ui.close() }]); return; }
  if (pr.stage === 'report') { report(ctx, k); return; }
  if (!pr.introSeen) {
    pr.introSeen = true; ctx.save();
    const sections = [
      { label: tr("你扮演"), items: [k.role], tone: 'info' as const },
      ...(k.dilemma ? [{ label: tr("核心兩難"), items: [k.dilemma], tone: 'warn' as const }] : []),
    ];
    ctx.ui.card({ name: k.title, role: tr("{0}｜{1}", k.locationText, k.types.join(tr("、"))), color: '#f2b134', badge: caseBadge(k),
      title: k.time ? tr("原始時間 {0}", k.time) : undefined, text: k.opening, sections,
      choices: [{ text: tr("和{0}說話", npc.shortName), tone: 'primary', fn: () => npcCard(ctx, k, npc.catchphrase, [{ text: tr("（開始處理）"), fn: () => openRound(ctx, k) }], { expression: 'worried' }) }] });
    return;
  }
  openRound(ctx, k);
}

function openRound(ctx: Ctx, k: Case): void {
  const pr = ctx.day.cases[k.id];
  const r = k.rounds[pr.round];
  if (!r) { finishRounds(ctx, k); return; }
  // 選項順序固定隨案件＋回合打亂（存檔重開也一樣）
  const seed = { rng: [...(k.id + pr.round + ctx.day.day)].reduce((s, ch) => (s * 31 + ch.charCodeAt(0)) | 0, 7) };
  const order = shuffle([0, 1], seed);
  const npc = ctx.c.npcById.get(k.npc)!;
  const speaker = r.speaker && !npc.shortName.includes(r.speaker) && !r.speaker.includes(npc.shortName) ? r.speaker : '';
  const text = (speaker ? tr("【{0}】", speaker) : '') + r.text;
  npcCard(ctx, k, text, order.map(i => ({ text: r.choices[i].text, fn: () => pick(ctx, k, i) })),
    { title: tr("第 {0}／{1} 回合{2}", pr.round + 1, k.rounds.length, r.title ? tr("｜{0}", r.title) : ''), expression: pr.round > 1 ? 'worried' : 'neutral' });
}

function pick(ctx: Ctx, k: Case, i: number): void {
  const pr = ctx.day.cases[k.id];
  const best = k.rounds[pr.round].choices[i].best;
  pr.picks.push(i);
  if (best) { pr.best++; ctx.apply({ records: 2 }, k.id); }
  else ctx.apply({ foodSafety: -3, records: -2 }, k.id);
  ctx.advance(best ? k.minutesPerRound.best : k.minutesPerRound.other, tr("案件處理"));
  if (pr.round === 0 && best && k.line && k.stopsLineOnBest) {
    ctx.day.lines[k.line] && (ctx.day.lines[k.line].stoppedBy = k.id);
    pr.stopped = true;
    ctx.ui.toast(tr("⏸ {0}線暫停（相關產品先隔離）", ctx.c.products.find(p => p.id === k.line)?.name ?? k.line));
  }
  pr.round++;
  ctx.save();
  if (pr.round >= k.rounds.length) finishRounds(ctx, k); else openRound(ctx, k);
}

function finishRounds(ctx: Ctx, k: Case): void {
  const pr = ctx.day.cases[k.id];
  pr.stage = 'inspect'; ctx.save();
  ctx.ui.close();
  ctx.ui.toast(tr("目標：到「{0}」確認", shortName(k.targetName)));
  ctx.world.refreshActors();
}

/** 走到目標機台 */
export function inspectTarget(ctx: Ctx, k: Case): void {
  if (maybeBeforeInspect(ctx, () => inspectTarget(ctx, k))) return;
  const pr = ctx.day.cases[k.id];
  ctx.checkpoint(tr("{0} 檢查前", k.targetName));
  const sections: NonNullable<Parameters<Ctx['ui']['card']>[0]['sections']> = [];
  if (k.targetResults.length) sections.push({ label: tr("操作後你看到"), items: k.targetResults, tone: 'info' });
  if (k.scene.length) sections.push({ label: tr("現場擺設"), items: k.scene });
  if (k.risk) {
    sections.push({ label: tr("現場觀察（環境／故障）"), items: [k.risk.environment, k.risk.failure], tone: 'info' });
    sections.push({ label: tr("重工因子"), items: [k.risk.rework], tone: 'warn' });
    sections.push({ label: tr("死亡因子・工傷事件"), items: [k.risk.fatal, k.risk.injury], tone: 'bad' });
    sections.push({ label: tr("可能涉及的食品法規"), items: [k.risk.regulation] });
  }
  for (const h of k.hazards) if (!ctx.profile.hazardsSeen.includes(h)) ctx.profile.hazardsSeen.push(h);
  const goodSoFar = pr.best / Math.max(1, pr.picks.length) >= ctx.c.game.cases.goodThreshold;
  ctx.ui.card({ name: k.targetName, role: tr("目標機台"), color: '#f2b134', badge: caseBadge(k),
    text: goodSoFar ? tr("紀錄、批號與設備狀態都調出來了。你把需要隔離的範圍標示清楚，拍照存證。")
      : tr("你調出紀錄，才發現有些資料在剛才的處理過程中已經斷掉了。能補的你都補上了。"),
    sections, wide: true,
    choices: [{ text: tr("記錄結果，回去回報"), tone: 'primary', fn: () => {
      pr.stage = 'report'; ctx.advance(5, tr("檢查")); ctx.save(); ctx.ui.close(); ctx.world.refreshActors();
      ctx.ui.toast(tr("目標：回報{0}", ctx.c.npcById.get(k.npc)!.shortName));
    } }] });
}

function report(ctx: Ctx, k: Case): void {
  const pr = ctx.day.cases[k.id];
  const ratio = pr.best / k.rounds.length;
  const grade: 'good' | 'bad' = ratio >= ctx.c.game.cases.goodThreshold ? 'good' : 'bad';
  const review = k.rounds.map((r, i) => {
    const mine = r.choices[pr.picks[i]]; const better = r.choices.find(x => x.best)!;
    return mine?.best ? tr("✔ {0}：{1}", r.title || tr("第 {0} 回合", i + 1), mine.text) : tr("✘ {0}：你選「{1}」→ 較佳：「{2}」", r.title || tr("第 {0} 回合", i + 1), mine?.text ?? '—', better.text);
  });
  const end = grade === 'good' ? k.endings.good : k.endings.bad;
  npcCard(ctx, k, end.line, [{ text: tr("結案"), tone: 'primary', fn: () => closeCase(ctx, k, grade) }], {
    title: tr("{0}{1}（較佳判斷 {2}/{3}）", grade === 'good' ? tr("好結局") : tr("壞結局"), end.title ? tr("｜{0}", end.title) : '', pr.best, k.rounds.length),
    expression: grade === 'good' ? 'happy' : 'worried',
    sections: [
      ...(k.successDefinition ? [{ label: tr("做對的定義"), items: [k.successDefinition], tone: 'good' as const }] : []),
      { label: tr("你的判斷回顧"), items: review, tone: grade === 'good' ? 'good' as const : 'bad' as const },
    ], wide: true,
  });
}

function closeCase(ctx: Ctx, k: Case, grade: 'good' | 'bad'): void {
  const { day, profile } = ctx;
  const pr = day.cases[k.id];
  pr.stage = 'done'; pr.grade = grade;
  gainXp(ctx, grade === 'good' ? 'caseGood' : 'caseBad', tr("案件 {0}{1}", k.id, grade === 'good' ? tr(" 好結局") : ''));
  const eff = k.outcome[grade];
  ctx.apply(eff, k.id);
  const note = settleSuspect(ctx, k.line, grade, eff.scrapPercent, eff.reworkMinutes);
  if (note) ctx.log(note, 'bad');
  if (grade === 'bad' && eff.safetyIncident) day.flags.caseSafetyIncident = true;
  if (grade === 'bad' && (eff.scrapPercent || eff.reworkMinutes)) day.flags.caseFoodIncident = true;
  ctx.log(tr("{0}「{1}」{2}（{3}/{4}）", k.id, k.title, grade === 'good' ? tr("好結局") : tr("壞結局"), pr.best, k.rounds.length), grade === 'good' ? 'good' : 'bad');
  const prev = profile.cleared[k.id];
  if (!prev || prev.grade === 'bad') profile.cleared[k.id] = { grade, best: pr.best, total: k.rounds.length, day: day.day };
  if (grade === 'good') { profile.goodTotal++; bump(ctx, 'casesGood'); }
  bump(ctx, 'casesClosed');
  day.activeCase++;
  const next = activeCase(ctx);
  if (next) day.cases[next.id].stage = 'talk';
  ctx.ui.close(); ctx.world.refreshActors(); ctx.save();
  if (note) ctx.ui.toast('⚠ ' + note);
  else if (next) ctx.ui.toast(tr("新的異常通報：{0}・{1}", ctx.c.floorById.get(next.floor)!.name, shortName(next.locationText.split(tr("・")).pop() ?? '')));
  else ctx.ui.toast(tr("今天的案件都處理完了。時間到記得去 1F 打卡。"));
  // 道具：前輩護身符／同事偷塞的綠色乖乖
  if (grade === 'good' && profile.goodTotal >= 2) grantItem(ctx, 'charm-senior');
  else if (grade === 'good' && !profile.items.includes('guaiguai') && nextRandom(day) < 0.3) grantItem(ctx, 'guaiguai');
  ctx.log(tr("{0} 結案", fmtClock(day.clock)), 'info');
}
